import React, { memo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Popover,
  FormControlLabel,
  Switch,
  SvgIcon,
  TextField,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import { DatePicker } from "rsuite";
import { Search } from "@material-ui/icons";
import { ErrorMessage, Formik } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { Upload } from "@mui/icons-material";
import usePermissions from "../../../hooks/usePermissions";

const DashboardFilter = memo(
  ({
    businesOptions,
    onApplyFilter,
    showExport,
    onExportReport,
    presentationMode,
    onPresentationModeChange,
    getBusinessLabel,
  }) => {
    const { businessId, isBusiness } = usePermissions();
    const colorSet = useSelector((state) => state.color);
    const currentBusinessName =
      Array.isArray(businesOptions) && businesOptions.length > 0
        ? getBusinessLabel(businesOptions[0]) || "Your Business"
        : presentationMode && businessId
          ? getBusinessLabel({ businessId })
          : "Your Business";
    const [anchorEl, setAnchorEl] = useState(null);
    const [savedFilters, setSavedFilters] = useState({
      business: null,
      startDate: null,
      endDate: null,
    });

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const open = Boolean(anchorEl);

    // Validation Schema
    const businessValidationSchema = Yup.object().shape({
      business: Yup.mixed().when([], {
        is: () => !isBusiness || !businessId,
        then: Yup.object()
          .required("Business is required")
          .test(
            "is-not-empty",
            "Business is required",
            (value) => value && Object.keys(value).length > 0
          ),
        otherwise: Yup.mixed().notRequired(),
      }),
      startDate: Yup.date()
        .required("Start date is required")
        .typeError("Start date is required"),
      endDate: Yup.date()
        .required("End date is required")
        .typeError("End date is required")
        .min(Yup.ref("startDate"), "End date cannot be before the start date"),
    });

    const handleSubmit = (values) => {
      setSavedFilters(values);
      onApplyFilter(values);
      setAnchorEl(null);
    };

    return (
      <Box display="flex" flexWrap="wrap" justifyContent="flex-end" alignItems="center" mb={2}>
        <Stack sx={{ mr: "auto" }}>
          <FormControlLabel
            control={
              <Switch
                checked={presentationMode}
                onChange={(_, checked) => onPresentationModeChange(checked)}
              />
            }
            label="Presentation mode"
          />
          {presentationMode && (
            <Typography variant="caption" color="warning.main">
              Exports contain original data
            </Typography>
          )}
        </Stack>
        {showExport && (
          <Box
            display="flex"
            alignItems="center"
            gap={0.5}
            onClick={onExportReport}
            sx={{ cursor: "pointer" }}
          >
            <IconButton>
              <Upload />
            </IconButton>
            <span style={{ fontSize: "14px" }}>Export</span>
          </Box>
        )}
        <Box
          display="flex"
          alignItems="center"
          gap={0.5}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ cursor: "pointer" }}
        >
          <IconButton color="default">
            <FilterListIcon />
          </IconButton>
          <span style={{ fontSize: "14px" }}>Build a report</span>
        </Box>
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            sx: {
              p: isMobile ? 2 : 3,
              width: isMobile ? "90vw" : 400,
              maxWidth: "100%",
            },
          }}
        >
          <Formik
            enableReinitialize
            initialValues={savedFilters}
            validationSchema={businessValidationSchema}
            onSubmit={handleSubmit}
          >
            {(formik) => {
              const { values, touched, errors, setFieldValue, handleSubmit } =
                formik;

              return (
                <form onSubmit={handleSubmit}>
                  <Stack spacing={2}>
                    {/* Business Field */}
                    {isBusiness && businessId ? (
                      <TextField
                        value={currentBusinessName}
                        fullWidth
                        disabled
                        label="Business"
                      />
                    ) : (
                      <Autocomplete
                        id="business"
                        name="business"
                        options={
                          Array.isArray(businesOptions) ? businesOptions : []
                        }
                        getOptionLabel={getBusinessLabel}
                        isOptionEqualToValue={(option, value) =>
                          (option?.businessId || option?.id) ===
                          (value?.businessId || value?.id)
                        }
                        value={values?.business || null}
                        onChange={(event, newValue) => {
                          setFieldValue("business", newValue || null);
                        }}
                        noOptionsText="No business found"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Business"
                            error={touched.business && !!errors.business}
                            helperText={touched.business && errors.business}
                          />
                        )}
                      />
                    )}

                    {/* Start Date */}
                    <Box>
                      <DatePicker
                        placeholder="Start Date"
                        name="startDate"
                        onChange={(value) => {
                          setFieldValue("startDate", value || null);
                        }}
                        value={values?.startDate || null}
                        size="lg"
                        style={{ width: "100%" }}
                        oneTap
                        cleanable
                      />
                      <ErrorMessage
                        name="startDate"
                        component="div"
                        style={{ color: "red", fontSize: 12 }}
                      />
                    </Box>

                    {/* End Date */}
                    <Box>
                      <DatePicker
                        placeholder="End Date"
                        name="endDate"
                        onChange={(value) => {
                          setFieldValue("endDate", value || null);
                        }}
                        value={values?.endDate || null}
                        size="lg"
                        style={{ width: "100%" }}
                        oneTap
                        cleanable
                        shouldDisableDate={(date) => {
                          const startDate = values?.startDate;
                          return startDate ? date < new Date(startDate) : false;
                        }}
                      />
                      <ErrorMessage
                        name="endDate"
                        component="div"
                        style={{ color: "red", fontSize: 12 }}
                      />
                    </Box>

                    {/* Submit Button */}
                    <Button
                      fullWidth
                      variant="contained"
                      type="submit"
                      startIcon={
                        <SvgIcon>
                          <Search />
                        </SvgIcon>
                      }
                      sx={{
                        backgroundColor: colorSet.color,
                        color: "white",
                        ":hover": { backgroundColor: colorSet.color },
                        mt: 1,
                      }}
                    >
                      Build
                    </Button>
                  </Stack>
                </form>
              );
            }}
          </Formik>
        </Popover>
      </Box>
    );
  }
);

export default DashboardFilter;
